import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { Switch } from '@/kit/Switch';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import type { CharModeOptions } from '@/kit/inputCharMode';
import { LeaveGuard } from '@/kit/LeaveGuard';
import { Radio, RadioGroup } from '@/kit/Radio';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import type { DialogComponentProps } from '@/kit/dialog';
import type { ReportOptionItem, ReportOptionLists, ReportSettingsView } from '@/core/models/report-options.models';
import type {
  CompanyInfoSettings,
  RegisteredInProfile,
  ReportingCurrencyChange,
} from '@/core/models/report-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { reportOptionsService } from '@/core/services/reportOptions';
import { reportSettingsService } from '@/core/services/reportSettings';
import { isAuthCodeInvalid, sanitizeAuthCode } from '@/core/utils/authCode';
import { companiesHouseLimitProblem, companiesHouseMaxLength } from '@/core/utils/companiesHouseLimits';
import { POSTCODE_PATTERN } from '@/features/manage/formRules';
import { setClientCommunityInterest } from '@/features/taxreturn/menus/menus';
import { isAuditedStatus, setClientAudited } from './auditStatus';
import { useReportClient } from './common';
import { StickyReportSettingsFrame } from './StickyReportSettingsFrame';
import { ClientKind, isUnincorporated, useClientKind } from './clientKind';
import { companySizeChoices } from './companySizeOptions';
import { taxonomyChoices } from './taxonomyOptions';
import './ReportSetting.scss';

type TextKey =
  | 'registeredName'
  | 'registrationNumber'
  | 'registeredOffice'
  | 'registeredOffice2'
  | 'city'
  | 'county'
  | 'postCode'
  | 'tradingName'
  | 'tradingOffice'
  | 'tradingOffice2'
  | 'tradingCity'
  | 'tradingCounty'
  | 'tradingPostCode';

/** The Report Information fields that are Report Settings' own values (ClientReportOptions), shared with that screen. */
type ReportNameKey = 'annualReportName' | 'managementReportName' | 'abbreviatedReportName' | 'filedReportName';
type ReportPickKey =
  | 'companySize'
  | 'companyLimitedBy'
  | 'taxonomy'
  | 'companyType'
  | 'incomeStatementName'
  | 'financialPositionName'
  | 'currencyCode';
type ReportFlagKey =
  | 'tangibleAssetsNote'
  | 'intangibleAssetsNote'
  | 'debtorsNote'
  | 'creditorsNote'
  | 'detailedProfitAndLoss'
  | 'showOperatingProfit'
  | 'showInterestPayable'
  | 'showTaxOnProfit'
  | 'showCreditorsAfterOneYear'
  | 'showCreditorsWithinOneYear'
  | 'showRetainedEarnings'
  | 'showDateApproved'
  | 'priorYearRestated'
  | 'membersInterest'
  | 'discloseDebtorsAfterOneYear'
  | 'showResignedDirectors';

const EMPTY_REPORT_OPTIONS: ReportOptionLists = {
  companySizes: [],
  companyLimitedBy: [],
  taxonomies: [],
  companyTypes: [],
  incomeStatementNames: [],
  financialPositionNames: [],
  currencies: [],
  shareTypes: [],
  shareClasses: [],
};

/** ClientReportOptions.AuditStatus (cap3-f) and TradingStatus (ap5-a5), as Report Settings uses them. */
const AUDIT_EXEMPT = 1;
const AUDITED = 2;
const DORMANT = 1;

/** The Income Statement Name code of the Income and Expenditure Account (db/seed/0260_app.reportoptionlookup.sql). */
const CIC_INCOME_STATEMENT = 'IE';
/** Issue #22: a CIC's Statement of Financial Position Name is fixed as the Balance Sheet (its code in the same lookup). */
const CIC_FINANCIAL_POSITION = 'BS';

/** 3.0's tip beside "Is this a CIC?". */
const CIC_HINT =
  'Please note that an annual Community Interest Company report has to be filed with the accounts. You can edit the Community Interest Company report from Settings > CIC34 Report.';

/** BUG-025: the application's default reporting currency (ReportOptionDefaults.CurrencyCode) - the one a Registered In
 * option's default currency may replace. */
const REPLACEABLE_CURRENCY = 'GBP';

/** BUG-025: a currency as the Currency Type list names it (the UAE dirham with its symbol). */
const currencyName = (code: string) => (code === 'AED' ? 'AED (د.إ)' : code);

/** #77: shown under the auth code while it is partly typed, and as the reason Save is refused. */
const AUTH_CODE_MESSAGE = 'Enter 6 alphanumeric characters.';

/** The Companies House company number: 8 letters or digits. */
const COMPANY_NUMBER_PATTERN = /^[A-Za-z0-9]{8}$/;

/** The Registered In values that are in the UK; a blank value is the default, England and Wales. */
const UK_REGISTERED_IN = ['england and wales', 'wales', 'scotland', 'northern ireland', 'uk'];
const isUkRegisteredIn = (registeredIn: string | null | undefined): boolean => {
  const value = (registeredIn ?? '').trim().toLowerCase();
  return !value || UK_REGISTERED_IN.includes(value);
};

const createUkProfile = (value: string, sortOrder: number, wording?: string | null): RegisteredInProfile => ({
  value,
  name: value,
  sortOrder,
  isActive: true,
  registeredNameLabel: 'Registered Name',
  registrationNumberLabel: 'Registration Number',
  registeredOfficeLabel: 'Registered Office',
  showCity: true,
  showCounty: true,
  showPostCode: true,
  addressStyle: 'Postal',
  incorporationWording: wording ?? null,
  defaultCurrencyCode: null,
  requiresCompaniesHouseNumber: true,
});

export const STANDARD_REGISTERED_IN_PROFILES: RegisteredInProfile[] = [
  createUkProfile('England and Wales', 10),
  // BUG-025: retired (the old application has no 'Wales'); a client that already holds it keeps it (heldRegisteredIn).
  { ...createUkProfile('Wales', 20), isActive: false },
  createUkProfile('Scotland', 30),
  createUkProfile('Northern Ireland', 40),
  createUkProfile('UK', 50, 'incorporated in the United Kingdom'),
  {
    ...createUkProfile('UAE', 60, 'incorporated in the United Arab Emirates'),
    registeredNameLabel: 'Company Name',
    registrationNumberLabel: 'Trade license',
    registeredOfficeLabel: 'Address of the entity',
    showCity: false,
    showCounty: false,
    showPostCode: false,
    addressStyle: 'Lines',
    defaultCurrencyCode: 'AED',
    requiresCompaniesHouseNumber: false,
  },
  createUkProfile('Non-resident', 70, 'incorporated outside the United Kingdom'),
];

export const DEFAULT_REGISTERED_IN_PROFILE = STANDARD_REGISTERED_IN_PROFILES[0];

function mergeProfiles(
  serverProfiles?: RegisteredInProfile[] | null,
  serverOptions?: string[] | null,
): RegisteredInProfile[] {
  const map = new Map<string, RegisteredInProfile>();
  for (const p of STANDARD_REGISTERED_IN_PROFILES) {
    map.set(p.value.toLowerCase(), p);
  }
  if (serverOptions) {
    serverOptions.forEach((opt, idx) => {
      const key = opt.toLowerCase();
      if (!map.has(key)) {
        map.set(key, createUkProfile(opt, 100 + idx));
      }
    });
  }
  if (serverProfiles && serverProfiles.length > 0) {
    for (const p of serverProfiles) {
      map.set(p.value.toLowerCase(), p);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

/** #88: dormant accounts are audit exempt; an older record saved as dormant and audited opens as audit exempt. */
const normaliseReport = (loaded: ReportSettingsView): ReportSettingsView =>
  loaded.tradingStatus === DORMANT && loaded.auditStatus === AUDITED
    ? { ...loaded, auditStatus: AUDIT_EXEMPT, reportType: 1 }
    : loaded;

/**
 * Report Settings > Company Info (Angular TaxreturnReportsettingsComponent). A routed page AND a dialog opened from
 * Settings, so it accepts optional dialog props. BUG-028: fields are always editable (3 Nov design review, persistent
 * action bar); Discard reverts every change back to what was last saved and Save stores them - nothing is saved any
 * other way. Leaving the page, or closing the tab, with unsaved changes asks first (`LeaveGuard`).
 *
 * 3.0 parity (Accounts/Settings/Report/Company.aspx): the page has 3.0's two sections - Company (LLP, Business)
 * Information on the left, with the trading address, the Balance Sheet Less Disclosures and the Reporting Currency, and
 * Report Information on the right, with the report names, size, taxonomy, statement names and report options, each shown
 * to the client types 3.0 shows it to. The Report Information fields, the disclosures and the currency are the same
 * stored values as Report Settings > Report Settings (ReportOptions/getReportSettings, saveReportSettings): the two
 * screens read and write one record.
 */
export function CompanyInfo({ dialogRef }: Partial<DialogComponentProps<unknown, unknown>>) {
  const navigate = useNavigate();
  const client = useReportClient(() => dialogRef?.close());
  // BUG-078: a sole trader or partnership has a business, not a company: no registration number, jurisdiction or auth code.
  const clientKind = useClientKind(client?.id);
  const clientType = clientKind?.clientType;
  const business = isUnincorporated(clientType);
  // A sole trader's accounts do not follow the FRC reporting taxonomies, so there is nothing to choose. Nor does a
  // partnership's (issue #24).
  const isSoleTrader = clientType === ClientKind.SoleTrader;
  const isPartnership = clientType === ClientKind.Partnership;
  // BUG-072: an LLP's page is "LLP Info" - its members, not directors, and the Members' Interest option.
  const isLlp = clientType === ClientKind.Llp;
  /** 3.0's "Limited": a limited company (also while the client's type is still being read). */
  const isLimited = !business && !isLlp;

  const [info, setInfoState] = useState<CompanyInfoSettings | null>(null);
  const [registeredInOptions, setRegisteredInOptions] = useState<string[]>(() =>
    STANDARD_REGISTERED_IN_PROFILES.map((p) => p.value),
  );
  /** BUG-025: the Registered In values last loaded or saved - a retired option among them is still offered. */
  const [heldRegisteredIn, setHeldRegisteredIn] = useState<string[]>([]);
  // BUG-025: each Registered In option's behaviour (labels, address fields), and that of a blank or unknown value.
  const [registeredInProfiles, setRegisteredInProfiles] = useState<RegisteredInProfile[]>(
    () => STANDARD_REGISTERED_IN_PROFILES,
  );
  const [defaultRegisteredInProfile, setDefaultRegisteredInProfile] = useState<RegisteredInProfile | null>(
    () => DEFAULT_REGISTERED_IN_PROFILE,
  );
  const [isDifferentTradingAddress, setIsDifferentTradingAddress] = useState(false);
  /** The report settings (Report Information, disclosures, currency); null until loaded - their fields wait for it. */
  const [report, setReportState] = useState<ReportSettingsView | null>(null);
  const [reportLists, setReportLists] = useState<ReportOptionLists>(EMPTY_REPORT_OPTIONS);
  const [authCodeDirty, setAuthCodeDirtyState] = useState(false);
  /** The `required` inputs draw the red outline once they have been touched and are empty (Angular ng-invalid ng-touched). */
  const [touched, setTouched] = useState<ReadonlySet<TextKey>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  /**
   * BUG-028: whether the form differs from what was last loaded or saved. A derived value, but `savedInfo`/`savedReport`
   * are refs (read outside render, in handlers and the save flow), so it is recomputed imperatively wherever `info`,
   * `report` or the saved baselines change, rather than read from a ref during render (react-hooks/refs).
   */
  const [isDirty, setIsDirty] = useState(false);

  // Latest values for the handlers, which can run before the next render.
  const infoRef = useRef<CompanyInfoSettings | null>(null);
  /** BUG-028: the company info as last loaded or saved - what Cancel restores, every field of it. */
  const savedInfo = useRef<CompanyInfoSettings | null>(null);
  const reportRef = useRef<ReportSettingsView | null>(null);
  /** The report settings as last loaded or saved - what Discard restores. */
  const savedReport = useRef<ReportSettingsView | null>(null);
  const savingRef = useRef(false);
  /** #77: set once the user types in the field, so a legacy value is never flagged just for being there. */
  const authCodeDirtyRef = useRef(false);
  /** Moves the caret to the first field when editing starts in the dialog (it opens read-only, so nothing has focus). */
  const officeInputRef = useRef<HTMLInputElement>(null);

  const setInfo = (next: CompanyInfoSettings | null) => {
    infoRef.current = next;
    setInfoState(next);
    recomputeDirty();
  };
  const setReport = (next: ReportSettingsView | null) => {
    reportRef.current = next;
    setReportState(next);
    recomputeDirty();
  };
  const setAuthCodeDirty = (value: boolean) => {
    authCodeDirtyRef.current = value;
    setAuthCodeDirtyState(value);
  };

  const authCodeInvalidNow = () => authCodeDirtyRef.current && isAuthCodeInvalid(infoRef.current?.authCode);
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const infoChanged = () => !!infoRef.current && !!savedInfo.current && !same(infoRef.current, savedInfo.current);
  const reportChanged = () =>
    !!reportRef.current && !!savedReport.current && !same(reportRef.current, savedReport.current);
  /** Recomputes `isDirty` from the refs - called wherever `info`, `report` or the saved baselines change. */
  const recomputeDirty = () => setIsDirty(infoChanged() || reportChanged());

  /** BUG-025: the save moved the reporting currency because Registered In changed (UAE: the UAE dirham). */
  const announceCurrencyChange = (changed: ReportingCurrencyChange, registeredIn: string | null) =>
    notifySuccess(
      `Currency type changed to ${changed.name}${registeredIn ? ` for Registered In ${registeredIn}` : ''}.`,
    );

  /** BUG-025: the currency a Registered In option moves the report to (UAE: AED), or null when it has none. */
  const defaultCurrencyOf = (registeredIn: string | null | undefined): string | null => {
    const selected = (registeredIn ?? '').trim().toLowerCase();
    if (!selected) return null;
    return (
      registeredInProfiles.find((profile) => profile.value.trim().toLowerCase() === selected)?.defaultCurrencyCode ??
      null
    );
  };

  /**
   * BUG-025 / 3.0 RegisteredInUpdate: the Currency type the form follows when Registered In changes - an option with a
   * default currency (UAE: AED) takes a client still on the application's default currency (or none) to it; leaving
   * such an option while still on its currency goes back to the default. A currency the preparer chose is kept. The
   * rule usp_ReportCompanyInfo_Save applies on Save.
   */
  const currencyFollowing = (previous: string | null, next: string | null, current: string | null | undefined) => {
    const target = defaultCurrencyOf(next);
    if (target) return !current || current === REPLACEABLE_CURRENCY ? target : current;
    const previousDefault = defaultCurrencyOf(previous);
    if (previousDefault && current === previousDefault) return REPLACEABLE_CURRENCY;
    return current ?? null;
  };

  /**
   * BUG-028: the reporting currency the save would move to `registeredIn`'s default currency - only while the client is
   * still on the application's default currency (or has none saved). Read-only: nothing is saved here. Null when the
   * currency would stay as it is. Used only when the report settings could not be loaded with the page.
   */
  const pendingCurrencyChange = async (registeredIn: string | null) => {
    const target = defaultCurrencyOf(registeredIn);
    if (!target || !client) return null;
    const current = await reportOptionsService.getReportSettings(client.id);
    const code = current?.currencyCode;
    if ((code && code !== REPLACEABLE_CURRENCY) || code === target) return null;
    const changed: ReportingCurrencyChange = { code: target, name: currencyName(target) };
    return { changed, current };
  };

  /**
   * BUG-028: after Save, when the server did not move the currency itself (its Registered In options carry no default
   * currency yet) but the option chosen has one, the report settings follow it now. Used only when the report settings
   * could not be loaded with the page (otherwise the form's Currency type is saved with them).
   */
  const syncCurrencyAfterSave = async (
    registeredIn: string | null,
    previousRegisteredIn: string | null | undefined,
  ): Promise<ReportingCurrencyChange | null> => {
    if (!client || (registeredIn ?? '') === (previousRegisteredIn ?? '')) return null;
    try {
      const pending = await pendingCurrencyChange(registeredIn);
      if (!pending) return null;
      await reportOptionsService.saveReportSettings({ ...pending.current, currencyCode: pending.changed.code });
      return pending.changed;
    } catch {
      return null;
    }
  };

  /** BUG-027: the CIC flag is the client record's, so the Settings menu's CIC34 item follows what this page holds. */
  const syncCommunityInterest = (current: CompanyInfoSettings) => {
    if (current.canBeCommunityInterest && current.isCommunityInterest != null) {
      setClientCommunityInterest(current.clientId, current.isCommunityInterest);
    }
  };

  const holdRegisteredIn = (saved: CompanyInfoSettings) =>
    setHeldRegisteredIn(
      [saved.registeredIn, saved.tradingRegisteredIn].flatMap((value) => (value ? [value.trim().toLowerCase()] : [])),
    );

  /** Puts the page back to `saved`: every field, the trading address tick and the field states. */
  const restore = (saved: CompanyInfoSettings) => {
    setInfo(saved);
    setIsDifferentTradingAddress(saved.hasTradingAddress);
    setAuthCodeDirty(false);
    setTouched(new Set());
  };

  /** BUG-028 / TC 3419: Discard immediately reverts every field to the company info and report settings last loaded or saved. */
  const discard = () => {
    if (!savedInfo.current || savingRef.current) return;
    restore(savedInfo.current);
    if (savedReport.current) setReport(savedReport.current);
  };

  /**
   * BUG-028: the only save. It sends the company info once, then the report settings when any of theirs changed; a
   * failure keeps the page in edit mode with every unsaved change still there, so nothing typed is lost and Cancel
   * still goes back to what was last saved. #77: a partly typed auth code is not saved (nor silently swapped for the
   * old one) - Save is refused until it is complete or empty.
   */
  const save = async () => {
    const current = infoRef.current;
    if (!current || !savedInfo.current || !client || savingRef.current) return;
    if (authCodeInvalidNow()) {
      notifyError(AUTH_CODE_MESSAGE);
      return;
    }
    if (!business && current.registrationNumber && current.registrationNumber.trim().length !== 8) {
      notifyError('Company Registration Number should be 8 characters only.');
      return;
    }
    // A company or LLP registered in the UK (a blank Registered In counts as England and Wales): the Companies House
    // number is 8 letters or digits, and a post code that was changed is a UK post code.
    if (!business && isUkRegisteredIn(current.registeredIn)) {
      const number = (current.registrationNumber ?? '').trim();
      if (number && !COMPANY_NUMBER_PATTERN.test(number)) {
        notifyError('Company Registration Number should be 8 characters only.');
        return;
      }
      for (const key of ['postCode', 'tradingPostCode'] as const) {
        const code = (current[key] ?? '').trim();
        if (code && code !== (savedInfo.current[key] ?? '').trim() && !POSTCODE_PATTERN.test(code)) {
          notifyError(`'${code}' is not a valid UK post code.`);
          return;
        }
      }
    }
    // APT380: the Companies House limits on what the user changed (a value saved earlier that is already longer is left alone).
    const limitProblem = business ? null : companiesHouseLimitProblem(current, savedInfo.current);
    if (limitProblem) {
      notifyError(limitProblem);
      return;
    }
    const infoHasChanged = !same(current, savedInfo.current);
    if (!infoHasChanged && !reportChanged()) {
      // Nothing to save - Save was clicked (or auto-clicked) with no actual change.
      notifySuccess('Company information saved successfully.');
      return;
    }

    const payload: CompanyInfoSettings = { ...current };
    const previousRegisteredIn = savedInfo.current.registeredIn;
    const registeredInChanged = (payload.registeredIn ?? '') !== (previousRegisteredIn ?? '');
    savingRef.current = true;
    setIsSaving(true);
    try {
      let currencyChanged: ReportingCurrencyChange | null = null;
      if (infoHasChanged) {
        const saved = await reportSettingsService.saveCompanyInfo(payload);
        savedInfo.current = payload;
        holdRegisteredIn(payload);
        setAuthCodeDirty(false);
        setTouched(new Set());
        syncCommunityInterest(payload);
        currencyChanged = saved?.currencyChanged ?? null;
        // BUG-025: the server moved the currency in the same save - what the report settings now hold.
        if (currencyChanged && savedReport.current) {
          savedReport.current = { ...savedReport.current, currencyCode: currencyChanged.code };
        }
        if (!reportRef.current) {
          currencyChanged ??= await syncCurrencyAfterSave(payload.registeredIn, previousRegisteredIn);
        }
      }

      const formReport = reportRef.current;
      const before = savedReport.current;
      if (formReport && before && !same(formReport, before)) {
        await reportOptionsService.saveReportSettings({ ...formReport, clientId: client.id });
        savedReport.current = formReport;
        // #200: the Accountant's / Auditor's Report name in the menu follows the audit status saved here.
        setClientAudited(client.id, isAuditedStatus(formReport.auditStatus, formReport.tradingStatus));
        const code = formReport.currencyCode;
        if (!currencyChanged && registeredInChanged && code && code !== before.currencyCode) {
          if (code === defaultCurrencyOf(payload.registeredIn)) currencyChanged = { code, name: currencyName(code) };
        }
      }
      // A currency the preparer then changed on the form is theirs, not a move to announce.
      if (currencyChanged && formReport && formReport.currencyCode !== currencyChanged.code) currencyChanged = null;

      // The saved baselines moved (directly, not through setInfo/setReport) - the "Unsaved changes" hint follows.
      recomputeDirty();
      // BUG-025's message when the save also moved the reporting currency; the standard confirmation otherwise.
      if (currencyChanged) announceCurrencyChange(currencyChanged, payload.registeredIn);
      else notifySuccess('Company information saved successfully.');
    } catch (error) {
      notifyError(error);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  // As a dialog, fields are editable from the moment it opens, so the first field takes the caret once the record has
  // loaded - the dependency is a boolean so this runs only the once, not on every keystroke's state update.
  const hasInfo = info !== null;
  useEffect(() => {
    if (hasInfo && dialogRef) officeInputRef.current?.focus();
  }, [hasInfo, dialogRef]);

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    // A report-settings failure only hides their fields; Company Info itself still loads.
    Promise.resolve()
      .then(() => reportOptionsService.getReportSettings(client.id))
      .then(
        (loaded) => {
          if (cancelled || !loaded) return;
          const normalised = normaliseReport(loaded);
          savedReport.current = normalised;
          setReport(normalised);
        },
        () => undefined,
      );
    Promise.resolve()
      .then(() => reportOptionsService.getOptions())
      .then(
        (lists) => {
          if (!cancelled && lists) setReportLists({ ...EMPTY_REPORT_OPTIONS, ...lists });
        },
        () => undefined,
      );
    Promise.all([reportSettingsService.getOptions(), reportSettingsService.getCompanyInfo(client.id)]).then(
      ([options, loaded]) => {
        if (cancelled) return;
        const merged = mergeProfiles(options.registeredInProfiles, options.registeredIn);
        setRegisteredInOptions(merged.map((p) => p.value));
        setRegisteredInProfiles(merged);
        setDefaultRegisteredInProfile(options.defaultRegisteredInProfile ?? DEFAULT_REGISTERED_IN_PROFILE);
        savedInfo.current = loaded;
        holdRegisteredIn(loaded);
        restore(loaded);
        syncCommunityInterest(loaded);
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    return () => {
      cancelled = true;
    };
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = (patch: Partial<CompanyInfoSettings>) => {
    if (!infoRef.current) return;
    setInfo({ ...infoRef.current, ...patch });
  };

  /**
   * A CIC is usually limited by guarantee, so ticking CIC moves a Shares (or empty) "Company Limited By" to Guarantee.
   * It stays editable, and unticking leaves whatever is chosen.
   */
  const onCicChange = (checked: boolean) => {
    if (!infoRef.current) return;
    setInfo({ ...infoRef.current, isCommunityInterest: checked });
    const held = reportRef.current?.companyLimitedBy?.trim().toUpperCase();
    if (checked && reportRef.current && (!held || held === 'SHARES')) {
      setReport({ ...reportRef.current, companyLimitedBy: 'GUARANTEE' });
    }
  };

  /** A change to a report setting; saved with the rest on Save. */
  const changeReport = (patch: Partial<ReportSettingsView>) => {
    if (!reportRef.current) return;
    setReport({ ...reportRef.current, ...patch });
  };

  /**
   * BUG-028: choosing a Registered In option only changes the form. One with a default currency (UAE: AED) moves the
   * form's Currency type with it (3.0 RegisteredInUpdate) and says so, but nothing is saved until Save, and Discard
   * leaves it as it was.
   */
  const onRegisteredInChange = (newVal: string | null) => {
    const previous = infoRef.current?.registeredIn ?? null;
    change({ registeredIn: newVal });
    const backToSaved = (newVal ?? '') === (savedInfo.current?.registeredIn ?? '');
    const formReport = reportRef.current;
    if (formReport) {
      const next = currencyFollowing(previous, newVal, formReport.currencyCode);
      if (!next || next === formReport.currencyCode) return;
      changeReport({ currencyCode: next });
      if (!backToSaved && next === defaultCurrencyOf(newVal)) {
        notifySuccess(`Currency type will change to ${currencyName(next)} for Registered In ${newVal} when you save.`);
      }
      return;
    }
    if (backToSaved) return;
    pendingCurrencyChange(newVal)
      .then((pending) => {
        if (pending && infoRef.current?.registeredIn === newVal) {
          notifySuccess(
            `Currency type will change to ${pending.changed.name} for Registered In ${newVal} when you save.`,
          );
        }
      })
      .catch(() => undefined);
  };

  /**
   * #77: keeps the code to the 6 letters/digits Companies House issues, upper case. Reads the caret off the input so a
   * mid-string edit stays in place, and takes the full pasted value before the maxlength could clip a formatted paste.
   */
  const onAuthCodeInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (!infoRef.current) return;
    const input = event.currentTarget;
    const caret = input.selectionStart ?? input.value.length;
    const sanitizedBeforeCaret = sanitizeAuthCode(input.value.slice(0, caret));
    const authCode = sanitizeAuthCode(input.value);
    setInfo({ ...infoRef.current, authCode });
    setAuthCodeDirty(true);
    const newCaret = Math.min(sanitizedBeforeCaret.length, authCode.length);
    queueMicrotask(() => input.setSelectionRange(newCaret, newCaret));
  };

  const onTradingAddressChange = (checked: boolean) => {
    if (!infoRef.current) return;
    setIsDifferentTradingAddress(checked);
    change({ hasTradingAddress: checked });
  };

  /** ap5-a5 (as Report Settings): choosing Dormant drops an audited choice that is no longer available. */
  const onTradingStatusChange = (tradingStatus: number) => {
    const patch: Partial<ReportSettingsView> = { tradingStatus };
    if (tradingStatus === DORMANT && reportRef.current?.auditStatus === AUDITED) {
      patch.auditStatus = AUDIT_EXEMPT;
      patch.reportType = 1;
    }
    changeReport(patch);
  };

  /** 3.0's Report Type "Audited" tick: the audit status (ReportType is kept consistent, as Report Settings does). */
  const onAuditedChange = (audited: boolean) =>
    changeReport({ auditStatus: audited ? AUDITED : AUDIT_EXEMPT, reportType: audited ? 0 : 1 });

  const close = () => {
    if (dialogRef) {
      dialogRef.close();
      return;
    }
    navigate('/home/taxreturns/dashboard');
  };

  const authCodeInvalid = authCodeDirty && isAuthCodeInvalid(info?.authCode);

  /**
   * BUG-025: the server's profile for the selected Registered In - matched as the server matches it (trimmed,
   * case-insensitive), the default profile for a blank or unknown value. Everything Registered In changes on this
   * page (labels, which address fields show) comes from it; the stored city, county and postcode are never cleared,
   * only not asked for, so changing back shows them again.
   */
  const registeredInProfile = (() => {
    const selected = (info?.registeredIn ?? '').trim().toLowerCase();
    return (
      (selected && registeredInProfiles.find((profile) => profile.value.trim().toLowerCase() === selected)) ||
      defaultRegisteredInProfile
    );
  })();
  const showCity = registeredInProfile?.showCity ?? true;
  const showCounty = registeredInProfile?.showCounty ?? true;
  const showPostCode = registeredInProfile?.showPostCode ?? true;
  const officeLabel = registeredInProfile?.registeredOfficeLabel ?? 'Registered Office';
  /**
   * The options offered, active ones in the server's order (by value when an older server sends no profiles). BUG-025:
   * a retired option the client already holds (Wales) is still offered to it, so it loads and saves unchanged.
   */
  const registeredInItems = registeredInProfiles.length
    ? registeredInProfiles
        .filter((profile) => profile.isActive || heldRegisteredIn.includes(profile.value.trim().toLowerCase()))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((profile) => ({ value: profile.value, name: profile.name }))
    : registeredInOptions.map((value) => ({ value, name: value }));

  // #413/#424 (as Report Settings): a CIC's income statement is its Income and Expenditure Account - here it follows
  // the CIC tick on this form.
  const isCic = info?.canBeCommunityInterest ? !!info.isCommunityInterest : !!clientKind?.isCommunityInterest;
  const isDormant = report?.tradingStatus === DORMANT;
  const entityWord = business ? 'Business' : isLlp ? 'LLP' : 'Company';
  // ap5-a5: the shared Small/Medium/Large Company size list reads "LLP" for an LLP (Micro Entity is unaffected).
  const companySizeItems = isLlp
    ? reportLists.companySizes.map((i) => ({ ...i, name: i.name.replace('Company', 'LLP') }))
    : reportLists.companySizes;

  const text = (
    key: TextKey,
    fieldClassName: string,
    extra?: { required?: boolean; 'aria-label'?: string } & CharModeOptions,
  ) => (
    <InputField
      ref={key === 'registeredOffice' ? officeInputRef : undefined}
      fieldClassName={fieldClassName}
      // APT380: a registered company or LLP is held to the Companies House limits (name 160, address lines 50, postcode 15).
      maxLength={(!business && companiesHouseMaxLength(key)) || 255}
      name={key}
      value={info?.[key] ?? ''}
      onChange={(event) => change({ [key]: event.target.value })}
      invalid={extra?.required && touched.has(key) && !info?.[key]}
      onBlur={extra?.required ? () => setTouched((current) => new Set(current).add(key)) : undefined}
      {...extra}
    />
  );

  const reportName = (key: ReportNameKey, label: string) => (
    <div className="form-field">
      <p>{label}</p>
      <InputField
        fieldClassName="w-434"
        maxLength={255}
        name={key}
        value={report?.[key] ?? ''}
        onChange={(event) => changeReport({ [key]: event.target.value })}
      />
    </div>
  );

  const reportPick = (key: ReportPickKey, label: string, items: ReportOptionItem[]) => {
    const cicIncome = isCic && key === 'incomeStatementName';
    const cicPosition = isCic && key === 'financialPositionName';
    return (
      <div className="form-field">
        <p>{label}</p>
        <Select
          className="w-260"
          name={key}
          clearable={false}
          disabled={cicIncome || cicPosition}
          items={items}
          bindLabel="name"
          bindValue="code"
          value={cicIncome ? CIC_INCOME_STATEMENT : cicPosition ? CIC_FINANCIAL_POSITION : report?.[key]}
          onChange={(value) => changeReport({ [key]: value as string })}
        />
      </div>
    );
  };

  const reportFlag = (key: ReportFlagKey, label: string, disabled = false) => (
    <Switch
      role="switch"
      name={key}
      disabled={disabled}
      checked={Boolean(report?.[key])}
      onChange={(event) => changeReport({ [key]: event.target.checked })}
      aria-label={key === 'membersInterest' ? "Member's Interest Members' Interest" : label}
      data-testid={key}
    >
      {label}
    </Switch>
  );

  return (
    <StickyReportSettingsFrame
      title={business ? 'Business Info' : isLlp ? 'LLP Info' : 'Company Info'}
      hostClassName="report-setting-host"
      onClose={close}
      dirty={isDirty}
      alwaysShowActions
      actionsPlacement="footer"
      busy={isSaving}
      onSave={() => void save()}
      onDiscard={discard}
    >
      {/* Coordinator clarification: the approved design wants Discard/Save persistently visible, not only once dirty
          (unlike the sibling Report Settings pages) - `alwaysShowActions` keeps them shown; `dirty` still drives the
          "Unsaved changes" wording and the LeaveGuard below. */}
      <LeaveGuard when={isDirty} />
      <div className="sidenav-body pt-1">
        {info && (
          <form noValidate onSubmit={(event) => event.preventDefault()}>
            <div className="sidenav-body">
              <div className="sidenav-body-content d-flex flex-wrap gap-20 p-0">
                {/* 3.0's left column: Company (LLP, Business) Information. */}
                <div className="ci-company">
                  <div className="form-field-title mb-10">
                    <p>{entityWord} Information</p>
                  </div>
                  <div className="wp-100">
                    <div>
                      <div className="d-flex gap-30">
                        <div className="form-field">
                          <p>
                            {business
                              ? 'Business Name'
                              : (registeredInProfile?.registeredNameLabel ?? 'Registered Name')}
                          </p>
                          {text('registeredName', 'w-260')}
                        </div>
                        {!business && (
                          <div className="form-field">
                            <p>{registeredInProfile?.registrationNumberLabel ?? 'Registration Number'}</p>
                            {text('registrationNumber', 'w-260', { charMode: 'alphanumeric' })}
                          </div>
                        )}
                      </div>

                      <div className="form-field">
                        <p>{business ? 'Business Address' : officeLabel}</p>
                        {text('registeredOffice', 'w-550', { required: true })}
                      </div>
                      {/* BUG-027: the registered office's optional second line. */}
                      <div className="form-field">
                        {text('registeredOffice2', 'w-550', {
                          'aria-label': `${business ? 'Business Address' : officeLabel} line 2`,
                        })}
                      </div>
                      {showCity && (
                        <div className="form-field">
                          <p>City/Town</p>
                          {text('city', 'w-550')}
                        </div>
                      )}
                      {(showCounty || showPostCode) && (
                        <div className="d-flex gap-30">
                          {showCounty && (
                            <div className="form-field">
                              <p>County</p>
                              {text('county', 'w-260')}
                            </div>
                          )}
                          {showPostCode && (
                            <div className="form-field">
                              <p>Postcode</p>
                              {text('postCode', 'w-260', { charMode: 'alphanumeric' })}
                            </div>
                          )}
                        </div>
                      )}

                      {!business && (
                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>Registered In</p>
                            <Select
                              className="w-260"
                              name="registeredIn"
                              items={registeredInItems}
                              bindLabel="name"
                              bindValue="value"
                              value={info.registeredIn}
                              onChange={(value) => onRegisteredInChange(value as string | null)}
                            />
                          </div>
                          <div className="form-field">
                            <p>Auth. Code</p>
                            {/* #77: a Companies House authentication code is exactly 6 alphanumeric characters. */}
                            <InputField
                              fieldClassName="w-260"
                              maxLength={20}
                              placeholder="6 characters"
                              name="authCode"
                              value={info.authCode ?? ''}
                              onChange={onAuthCodeInput}
                            />
                            {authCodeInvalid && <div className="text-red font-size-12">{AUTH_CODE_MESSAGE}</div>}
                          </div>
                        </div>
                      )}

                      {/* BUG-027: the client record's CIC flag (also on Add/Edit Client), offered where the server allows it. */}
                      {info.canBeCommunityInterest && (
                        <div className="form-field d-flex align-items-center gap-5">
                          <Switch
                            role="switch"
                            name="isCommunityInterest"
                            checked={!!info.isCommunityInterest}
                            onChange={(event) => onCicChange(event.target.checked)}
                          >
                            {/* BUG-027: 3.0's wording. */}
                            Is this a CIC?
                          </Switch>
                          <Tooltip title={CIC_HINT} placement="right">
                            <Icon name="info" outlined className="font-size-16 text-primary" />
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-field-title mb-1 d-flex align-items-center justify-space-between w-550">
                    <p className="m-0">Trading Address</p>
                    <Switch
                      role="switch"
                      checked={isDifferentTradingAddress}
                      onChange={(event) => onTradingAddressChange(event.target.checked)}
                    >
                      Different Trading Address
                    </Switch>
                  </div>
                  {/*
                    #188: the registration number and auth. code belong to the company and are entered once, above; the
                    trading address does not ask for them again. Any values already saved stay in the record and are
                    sent back unchanged with every save.
                  */}
                  {isDifferentTradingAddress && (
                    <div className="wp-100">
                      <div>
                        <div className="form-field">
                          <p>Trading Name</p>
                          {text('tradingName', 'w-260')}
                        </div>

                        {/* 3.0's trading address: two address lines, city, county and postcode. */}
                        <div className="form-field">
                          <p>Address</p>
                          {text('tradingOffice', 'w-550', { required: true })}
                        </div>
                        <div className="form-field">
                          {text('tradingOffice2', 'w-550', { 'aria-label': 'Trading Address line 2' })}
                        </div>
                        <div className="form-field">
                          <p>City/Town</p>
                          {text('tradingCity', 'w-550')}
                        </div>
                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>County</p>
                            {text('tradingCounty', 'w-260')}
                          </div>
                          <div className="form-field">
                            <p>Postcode</p>
                            {text('tradingPostCode', 'w-260', { charMode: 'alphanumeric' })}
                          </div>
                        </div>

                        {!business && (
                          <div className="form-field">
                            <p>Registered In</p>
                            <Select
                              className="w-260"
                              name="tradingRegisteredIn"
                              items={registeredInItems}
                              bindLabel="name"
                              bindValue="value"
                              value={info.tradingRegisteredIn}
                              onChange={(value) => change({ tradingRegisteredIn: value as string | null })}
                            />
                          </div>
                        )}
                        {/* 3.0 IsShowTradingAddress: the accounts' Company Information page prints the trading address. */}
                        <div className="form-field mb-1">
                          <Switch
                            role="switch"
                            name="showTradingAddress"
                            checked={!!info.showTradingAddress}
                            onChange={(event) => change({ showTradingAddress: event.target.checked })}
                          >
                            Show trading address on Company Information page?
                          </Switch>
                        </div>
                      </div>
                    </div>
                  )}

                  {report && (
                    <>
                      <div className="hr-top mb-1 pt-1">
                        <div className="form-field-title mb-10">
                          <p>Balance Sheet Less Disclosures</p>
                        </div>
                        <div className="d-flex flex-column">
                          {reportFlag('tangibleAssetsNote', 'Tangible Assets Note')}
                          {reportFlag('intangibleAssetsNote', 'Intangible Assets Note')}
                          {reportFlag('debtorsNote', 'Debtors Note')}
                          {reportFlag('creditorsNote', 'Creditors Note')}
                          {reportFlag('detailedProfitAndLoss', 'Detailed P&L account up to Profit before taxation')}
                        </div>
                      </div>
                      <div className="hr-top mb-1 pt-1">
                        <div className="form-field-title mb-10">
                          <p>Reporting Currency</p>
                        </div>
                        {reportPick('currencyCode', 'Currency type', reportLists.currencies)}
                      </div>
                    </>
                  )}
                </div>

                {/* 3.0's right column: Report Information - the report settings Report Settings also shows. */}
                {report && (
                  <div className="ci-report rs-left">
                    <div className="form-field-title mb-10">
                      <p>Report Information</p>
                    </div>
                    {reportName('annualReportName', 'Annual Report Name')}
                    {isLimited && reportName('managementReportName', 'Mgmt. Report Name')}
                    {/* APT341: no "Abbr. Report Name" any more - Abridged accounts can no longer be created (the saved value is kept as it is). */}
                    {/* ap5-a5: "filleted" is the statutory term for the accounts filed at Companies House. */}
                    {!business && reportName('filedReportName', 'Filleted Report Name')}
                    {!business && (
                      <div className="d-flex gap-20">
                        {!business &&
                          reportPick(
                            'companySize',
                            `${entityWord} Size`,
                            isLlp ? companySizeItems : companySizeChoices(companySizeItems, report?.companySize),
                          )}
                        {isLimited &&
                          reportPick('companyLimitedBy', 'Company Limited By', reportLists.companyLimitedBy)}
                      </div>
                    )}
                    {!isSoleTrader && !isPartnership && (
                      <div className="d-flex gap-20">
                        {reportPick(
                          'taxonomy',
                          'Use Taxonomy',
                          taxonomyChoices(reportLists.taxonomies, report?.companySize, report?.taxonomy),
                        )}
                        {isLimited && reportPick('companyType', 'Company Type', reportLists.companyTypes)}
                      </div>
                    )}
                    {reportPick('incomeStatementName', 'Income Statement Name', reportLists.incomeStatementNames)}
                    <div className="d-flex flex-column">
                      {reportFlag('showOperatingProfit', 'Operating Profit/Loss')}
                      {reportFlag('showInterestPayable', 'Interest Payable and similar charges')}
                      {reportFlag('showTaxOnProfit', 'Tax on profit on ordinary activities')}
                      {reportFlag('showCreditorsAfterOneYear', 'Creditors more than one year')}
                      {reportFlag('showCreditorsWithinOneYear', 'Creditors within one year')}
                      {reportFlag('showRetainedEarnings', 'Retained Earnings')}
                    </div>
                    <div className="mt-10">
                      {reportPick(
                        'financialPositionName',
                        'Statement of Financial Position Name',
                        reportLists.financialPositionNames,
                      )}
                    </div>
                    {/* APT289: "Dormant" is a Companies House filing status (Dormant Company Accounts) - it only
                        applies to incorporated entities (Limited, LLP), never to an unincorporated sole trader or
                        partnership, which file no statutory accounts (cap3-c Cap3EntityKind.Unincorporated). */}
                    {!business && (
                      <div className="form-field mb-5">
                        <p>Trading Status</p>
                        <RadioGroup
                          name="tradingStatus"
                          value={report.tradingStatus}
                          onChange={(value) => onTradingStatusChange(value as number)}
                        >
                          <Radio value={0} hostClassName="example-radio-button">
                            Trading
                          </Radio>
                          <Radio value={1} hostClassName="example-radio-button">
                            Dormant
                          </Radio>
                        </RadioGroup>
                      </div>
                    )}
                    <div className="d-flex flex-column">
                      {reportFlag('showDateApproved', 'Date Approved')}
                      {reportFlag('priorYearRestated', 'Previous year accounts restated')}
                      {isLlp && reportFlag('membersInterest', "Members' Interest")}
                    </div>
                    {isLlp && (
                      <p className="font-size-12 text-gray mb-0 mt-5">
                        When ticked, the LLP accounts include a note reconciling the members&apos; interests for the
                        year.
                      </p>
                    )}
                    {!business && (
                      <div className="form-field mt-10 mb-5">
                        {/* 3.0's Report Type: Audited. #88: dormant accounts are audit exempt. */}
                        <p>Report Type</p>
                        <div className="d-flex flex-column">
                          <Switch
                            role="switch"
                            name="audited"
                            disabled={isDormant}
                            checked={isAuditedStatus(report.auditStatus, report.tradingStatus)}
                            onChange={(event) => onAuditedChange(event.target.checked)}
                          >
                            Audited
                          </Switch>
                          {reportFlag(
                            'discloseDebtorsAfterOneYear',
                            'Disclose debtors more than one year in Balance Sheet',
                          )}
                        </div>
                      </div>
                    )}
                    {/* BUG-076: lists resigned directors (BUG-072: members) for the period. */}
                    {!business && (
                      <div className="form-field mb-1">
                        {reportFlag(
                          'showResignedDirectors',
                          isLlp ? 'Show resigned members' : 'Show resigned directors',
                        )}
                      </div>
                    )}
                    <p className="font-size-12 text-gray mb-0 mt-5">
                      Report Information, the Balance Sheet Less Disclosures and the Reporting Currency are the same
                      settings as Report Settings, and apply when the Accounts report is generated.
                    </p>
                  </div>
                )}
              </div>
            </div>

          </form>
        )}
      </div>
    </StickyReportSettingsFrame>
  );
}
